import React, { useMemo, useState } from 'react';

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import OutstandingOrdersTMSChildRow from './components/OutstandingOrdersTMSChildRow.jsx';
import OutstandingOrdersTMSHeader from './components/OutstandingOrdersTMSHeader.jsx';
import OutstandingOrdersTMSParentRow from './components/OutstandingOrdersTMSParentRow.jsx';

// =============================================================================
// BLOCK TMST.1 — Outstanding Orders TMS Table Component
// Main table component that displays orders with collapsible parent-child structure
// =============================================================================
function OutstandingOrdersTMSTable({ orders, sortBy, sortDir, onSort }) {
  // BLOCK TMST.1.1 — State for tracking expanded parents (keyed by SalesOrderNo)
  const [expandedParents, setExpandedParents] = useState({});

  // BLOCK TMST.1.2 — Toggle expand/collapse state for a parent order
  const handleToggleExpand = (salesOrderNo) => {
    setExpandedParents((prev) => ({
      ...prev,
      [salesOrderNo]: !prev[salesOrderNo],
    }));
  };

  // BLOCK TMST.1.3 — Group orders by SalesOrderNo to create parent-child structure
  const groupedOrders = useMemo(() => {
    const groups = {};

    // BLOCK TMST.1.3.1 — Iterate through all orders and group by SalesOrderNo
    orders.forEach((order) => {
      const { SalesOrderNo } = order;
      if (!groups[SalesOrderNo]) {
        groups[SalesOrderNo] = [];
      }
      groups[SalesOrderNo].push(order);
    });

    // BLOCK TMST.1.3.2 — Convert groups object to array of parent-child structures
    const result = Object.entries(groups).map(([salesOrderNo, lineItems]) => ({
      parent: lineItems[0], // Use first item as parent (summary row)
      children: lineItems, // All items are children with ItemCode/Qty
    }));

    return result;
  }, [orders]);

  // BLOCK TMST.1.4 — Render table container with collapsible parent-child rows
  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      {/* BLOCK TMST.1.5 — MUI Table wrapper */}
      <Table
        sx={{
          minWidth: 750,
          '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
        }}
        size="small"
        data-cy="outstanding-orders-tms-table-element"
      >
        {/* BLOCK TMST.1.6 — Table header */}
        <OutstandingOrdersTMSHeader sortBy={sortBy} sortDir={sortDir} onSort={onSort} />

        {/* BLOCK TMST.1.7 — Table body with parent rows and collapsible child rows */}
        <TableBody>
          {groupedOrders.map(({ parent, children }) => (
            <React.Fragment key={parent.SalesOrderNo}>
              {/* BLOCK TMST.1.8 — Render parent row with expand/collapse toggle */}
              <OutstandingOrdersTMSParentRow
                order={parent}
                isExpanded={expandedParents[parent.SalesOrderNo] || false}
                onToggleExpand={() => handleToggleExpand(parent.SalesOrderNo)}
              />

              {/* BLOCK TMST.1.9 — Render child rows if parent is expanded */}
              {expandedParents[parent.SalesOrderNo] &&
                children.map((child) => (
                  <OutstandingOrdersTMSChildRow
                    key={`${child.SalesOrderNo}-${child.LineKey}`}
                    order={child}
                  />
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default OutstandingOrdersTMSTable;
